import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ChevronLeft, Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ProductionDeclarationMaterialStatusBanner } from '@/features/production-declaration/components/statement/production-declaration-material-status-banner';
import { useCanManageSubmittedProductionDeclaration } from '@/features/production-declaration/hooks/use-can-manage-submitted-production-declaration';
import { useGetProductionDeclaration } from '@/services/apis/queries/production-declaration/use-get-production-declaration';
import { useGetProductionDeclarationMaterials } from '@/services/apis/queries/production-declaration/use-get-production-declaration-materials';

type ProductionDeclarationMaterialLayoutProps = {
  children?: ReactNode;
};

export function ProductionDeclarationMaterialLayout({
  children,
}: ProductionDeclarationMaterialLayoutProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { id, materialId } = useParams({ strict: false });
  const { token } = useSearch({ strict: false });
  const { t } = useTranslation('productionDeclaration');
  const productionDeclarationId = `${id}`;

  const { data: materials = [] } = useGetProductionDeclarationMaterials(
    productionDeclarationId,
    token,
  );
  const { data: declaration } = useGetProductionDeclaration(
    productionDeclarationId,
    token,
  );

  const selectedMaterial = materials.find(
    (material) => material.id === materialId,
  );
  const canEdit = useCanManageSubmittedProductionDeclaration(declaration);

  return (
    <div className='space-y-10'>
      <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
        <div className='flex items-center gap-4'>
          <button
            aria-label={t('materialProducts.backToMaterials')}
            className='inline-flex h-14 w-12 cursor-pointer items-center justify-center rounded-full text-declaration-foreground transition-colors hover:bg-declaration-hover'
            onClick={() =>
              navigate({
                params: { id: productionDeclarationId },
                search: { token },
                to: '/production-declarations/$id/statement',
              })
            }
            type='button'
          >
            <ChevronLeft className='size-10' strokeWidth={1.5} />
          </button>
          <div className='flex flex-wrap items-center gap-x-5 gap-y-2'>
            <h1 className='font-semibold text-4xl text-declaration-foreground uppercase tracking-tight'>
              {selectedMaterial?.materialEnglishName ?? t('materials.title')}
            </h1>
            {selectedMaterial?.materialScientificName ? (
              <p className='text-[1.15rem] text-declaration-foreground-muted tracking-tight'>
                {selectedMaterial.materialScientificName}
              </p>
            ) : selectedMaterial?.materialCommonName ? (
              <p className='text-[1.15rem] text-declaration-foreground-muted tracking-tight'>
                {selectedMaterial.materialCommonName}
              </p>
            ) : null}
          </div>
        </div>
        <p className='pt-2 text-[1.15rem] text-declaration-foreground-muted tracking-tight'>
          {t('materialProducts.hsCodeLabel')}{' '}
          {selectedMaterial?.materialHsCode ?? '—'}
        </p>
      </div>

      {isAuthenticated && selectedMaterial ? (
        <ProductionDeclarationMaterialStatusBanner
          canEdit={canEdit}
          code={selectedMaterial.status.code}
          materialId={selectedMaterial.id}
          productionDeclarationId={productionDeclarationId}
          token={token}
        />
      ) : null}

      <div className='flex items-center gap-2'>
        <h2 className='font-semibold text-[1.45rem] text-declaration-foreground tracking-tight'>
          {t('materialProducts.title')}
        </h2>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              aria-label={t('materialProducts.tooltip.title')}
              className='rounded-sm text-declaration-muted-foreground transition-colors hover:text-declaration-foreground-muted'
              type='button'
            >
              <Info aria-hidden className='size-5' />
            </button>
          </TooltipTrigger>
          <TooltipContent
            align='start'
            className='w-[min(40rem,calc(100vw-2rem))] space-y-3 rounded-xl border border-declaration-border bg-white p-4 text-left text-declaration-foreground text-sm leading-6 shadow-[0_10px_24px_rgba(17,24,39,0.08)] dark:border-declaration-border dark:bg-white dark:text-declaration-foreground'
            collisionPadding={24}
            showArrow={false}
            side='bottom'
            sideOffset={10}
          >
            <p className='font-semibold text-declaration-foreground text-sm'>
              {t('materialProducts.tooltip.title')}
            </p>
            <p>{t('materialProducts.tooltip.description')}</p>
          </TooltipContent>
        </Tooltip>
      </div>

      {children}
    </div>
  );
}
